import React from 'react';
import {
    DefaultListViewSharedProps,
    ListViewCommonProps,
    ListViewComposable, ListViewItemEventProps,
    ListViewItemsView
} from './list-view-composable';
import {
    IListView,
    isListViewDataSourceDataItem,
    ListViewChild,
    ListViewDataSourceDataItem,
    ListViewItemId,
    ListViewRenderItemProps,
    ListViewSelectionType,
    ListViewState,
    ListViewStateUpdateFunction,
    StateUpdateReason
} from './list-view-types';
import {DefaultListViewItemsViewProps, ListViewItemsViewProps} from './list-view-items-view';
import {arrayFindValue, arrayFlatten, PartiallyOptional} from '@wix/devzai-utils-common';
import {focusListView} from './list-view-utils';
import {RenderFunction} from '../index';

export type ListViewChildren<T> = ListViewChild<T>[];

/**
 * @typeparam T the type of a data item in a list view
 * @typeparam S the type of a context arg
 */
export interface ListViewProps<T> extends
    ListViewCommonProps<T>,
    ListViewItemsViewProps<T>,
    ListViewItemEventProps<T>
{
    children: ListViewChildren<T>;
}

export const DefaultListViewProps = {
    ...DefaultListViewSharedProps,
    ...DefaultListViewItemsViewProps
};

export type ListViewItemRenderFunction<T> = RenderFunction<ListViewRenderItemProps<T>, React.ReactElement>;

export class ListView<T> extends React.Component<ListViewProps<T>> implements IListView<T> {
    public static defaultProps = DefaultListViewProps;

    private listViewComposable = React.createRef<ListViewComposable>();

    public render() {
        const {
            children,
            renderItem,
            dataItemEqualityComparer,
            itemsWrapper,
            ...listViewComposableProps
        } = this.props;

        const dataSource = resolveListViewDataSourceItems(children);
        const normalizedDataSource = groupDataSourceDataItems(dataSource);

        const dataSourcesArray: ListViewDataSourceDataItem<T>[][] = [];

        const listViewComposableChildren = normalizedDataSource.map(itemsGroup => {
            if (Array.isArray(itemsGroup)) {
                const dataItemsArray = itemsGroup as ListViewDataSourceDataItem<T>[];

                dataSourcesArray.push(dataItemsArray);

                return (
                    <ListViewItemsView
                        dataSource={dataItemsArray}
                        renderItem={renderItem}
                        dataItemEqualityComparer={dataItemEqualityComparer}
                        itemsWrapper={itemsWrapper}
                    />
                );
            } else {
                return itemsGroup;
            }
        });

        return React.createElement.apply(this, [
            ListViewComposable,
            {
                ref: this.listViewComposable,
                dataSourcesArray: dataSourcesArray,
                ...listViewComposableProps
            },
            ...listViewComposableChildren
        ] as any);

        // return (
        //     <ListViewComposable
        //         ref={this.listViewComposable}
        //         dataSourcesArray={[dataSource]}
        //         {...listViewComposableProps}
        //     >
        //         {listViewComposableChildren}
        //     </ListViewComposable>
        // )
    }

    private withListViewComposable<RETURN>(callback: (listViewComposable: ListViewComposable) => RETURN): RETURN {
        const listViewComposable = this.listViewComposable.current;
        if (listViewComposable) {
            return callback(listViewComposable);
        } else {
            throw new Error(`method should be called only after rendering`);
        }
    }

    public findItemIndex(itemId: ListViewItemId) {
        return this.withListViewComposable(listViewComposable => listViewComposable.findItemIndex(itemId));
    }

    public focus() {
        focusListView(this);
    }

    public getItemsNodeList() {
        return this.withListViewComposable(listViewComposable => listViewComposable.getItemsNodeList());
    }

    public getItemsElements() {
        return this.withListViewComposable(listViewComposable => listViewComposable.getItemsElements());
    }

    public handleKeyboardEvent(event: React.KeyboardEvent<Element>) {
        return this.withListViewComposable(listViewComposable => listViewComposable.handleKeyboardEvent(event));
    }

    public moveToNextSearchMatchingItem(searchText: string, selectItem?: boolean) {
        return this.withListViewComposable(listViewComposable =>
            listViewComposable.moveToNextSearchMatchingItem(searchText, selectItem)
        );
    }

    public handleItemPointerInteraction(listViewItemId: ListViewItemId, event: React.MouseEvent<Element>) {
        this.withListViewComposable(listViewComposable =>
            listViewComposable.handleItemPointerInteraction(listViewItemId, event)
        );
    }

    public updateState(
        updateFunction: ListViewStateUpdateFunction,
        reason: StateUpdateReason = StateUpdateReason.Manual
    ) {
        return this.withListViewComposable(listViewComposable =>
            listViewComposable.updateState(updateFunction, reason)
        );
    }

    public getDataItemById(itemId: ListViewItemId) {
        return this.withListViewComposable(listViewComposable => listViewComposable.getDataItemById(itemId));
    }

    public getListViewItemById(itemId: ListViewItemId) {
        return this.withListViewComposable(listViewComposable => listViewComposable.getListViewItemById(itemId));
    }

    public getOrderedListViewItemsIds() {
        return this.withListViewComposable(listViewComposable => listViewComposable.getOrderedListViewItemsIds());
    }

    public getId() {
        return this.withListViewComposable(listViewComposable => listViewComposable.getId());
    }

    public getRootElement() {
        return this.withListViewComposable(listViewComposable => listViewComposable.getRootElement());
    }

    public getListViewState(): ListViewState {
        return this.withListViewComposable(listViewComposable => listViewComposable.getListViewState());
    }

    public getSelectionType(): ListViewSelectionType {
        return this.props.selectionType;
    }

    public getCurrentItemId() {
        return this.getListViewState().currentNavigatableItemId;
    }

    public getCurrentDataItem(): T | undefined {
        const currentDataSourceItem = this.getCurrentDataSourceItem();

        if (currentDataSourceItem !== undefined) {
            return currentDataSourceItem.dataItem;
        } else {
            return undefined;
        }
    }

    public getCurrentDataSourceItem(): ListViewDataSourceDataItem<T> | undefined {
        const currentItemId = this.getCurrentItemId();
        if (currentItemId !== null) {
            return this.getDataSourceItemById(currentItemId);
        } else {
            return undefined;
        }
    }

    public getDataSourceItemById(itemId: ListViewItemId): ListViewDataSourceDataItem<T> | undefined {
        return arrayFindValue(this.getListViewItemsArray(), dataSourceItem => dataSourceItem.id === itemId);
    }

    public getListViewItemsArray(): ListViewDataSourceDataItem<T>[] {
        return this.withListViewComposable(listViewComposable => listViewComposable.getListViewItemsArray());
    }
}

export function resolveListViewDataSourceItems<T>(children: ListViewChildren<T>): ListViewChild<T>[] {
    return Array.isArray(children) ? arrayFlatten(children as Array<any>) : [children];
}

export function groupDataSourceDataItems<T>(
    dataSourceItems: ListViewChild<T>[]
): (ListViewDataSourceDataItem<T>[] | React.ReactNode)[] {

    if (dataSourceItems.length === 0) {
        return [[]]
    }

    const result: (ListViewDataSourceDataItem<T>[] | React.ReactNode)[] = [];

    let currentGroup: ListViewDataSourceDataItem<T>[] | undefined = undefined;
    let lastItemIsDataItem: boolean | undefined = undefined;

    for (const dataSourceItem of dataSourceItems) {
        const currentItemIsDataItem = isListViewDataSourceDataItem(dataSourceItem);

        if (currentItemIsDataItem) {
            if (!lastItemIsDataItem) {
                currentGroup = [];
                result.push(currentGroup);
            }

            currentGroup!.push(dataSourceItem as any);
        } else {
            result.push(dataSourceItem);
        }

        lastItemIsDataItem = currentItemIsDataItem;
    }

    return result;
}

export namespace ListView {

    export type Props<T> = PartiallyOptional<ListViewProps<T>, keyof typeof DefaultListViewProps>;

}